// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/bancoIntegracao/providers/sicoobProvider.ts
//
//  Implementação do BancoIntegracaoProvider para o Sicoob (endpoints
//  /api/sicoob-*). Mapeia as respostas do Sicoob para os tipos neutros: o
//  "codigoSolicitacao" das telas é o NOSSO NÚMERO do boleto.
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type { BancoIntegracaoProvider } from '../provider';
import type {
  BancoSaldo, ExtratoSyncResultado, CobrancaOpcoes, CobrancaResultado,
  CobrancaSituacao, CobrancasEmitidasPagina, PixCobRequest, PixCobvRequest, PixCobResultado,
  PixDevolucaoResultado, WebhookTipo,
} from '../types';

interface SicoobBoletoResultado {
  sicoobBoletoId: number;
  nossoNumero: string;
  contaReceberId: number;
  valor: number;
  dtVencimento: string;
}

interface SicoobBoletoSituacao {
  nossoNumero: string;
  situacao: string | null;
  valorNominal: number;
  dtVencimento: string;
  linhaDigitavel: string | null;
  codigoBarras: string | null;
  pixCopiaECola: string | null;
  baixaAplicada: boolean;
}

/// O Sicoob só oferece webhook de boleto pago e de PIX recebido.
function tipoWebhook(tipo: WebhookTipo): 'Cobranca' | 'Pix' {
  if (tipo === 'Cobranca' || tipo === 'Pix') return tipo;
  throw new Error('O Sicoob não oferece webhook para este tipo.');
}

export const sicoobProvider: BancoIntegracaoProvider = {
  id: 'sicoob',
  nome: 'Sicoob',
  capabilities: { saldo: true, extrato: true, cobranca: true, pix: true, webhooks: true },
  webhookTipos: ['Cobranca', 'Pix'],
  webhookPlaceholder: 'https://api.udisysgestor.com',
  webhookAjuda:
    'Informe só o endereço público da API (HTTPS, porta 443). O Udisys monta o caminho completo, ' +
    'com o código de segurança, ao registrar no Sicoob.',
  rotuloCodigoCobranca: 'Nosso número',
  opcoesPixNoBoleto: true,
  cobrancaHerdaConta: true,

  // ── Saldo & Extrato ────────────────────────────────────────────────────────
  async consultarSaldo(contaCorrenteId) {
    const { data: r } = await apiClient.get<BancoSaldo>('/api/sicoob-extrato/saldo', {
      params: { contaCorrenteId },
    });
    return r;
  },

  async sincronizarExtrato(contaCorrenteId, dataInicio, dataFim) {
    const { data: r } = await apiClient.post<ExtratoSyncResultado>(
      '/api/sicoob-extrato/sincronizar',
      { contaCorrenteId, dataInicio, dataFim },
      { timeout: 90_000 },
    );
    return r;
  },

  // ── Cobrança (boleto) ───────────────────────────────────────────────────────
  async emitirCobranca(contaCorrenteId, contaReceberId, opcoes: CobrancaOpcoes): Promise<CobrancaResultado> {
    const { data: r } = await apiClient.post<SicoobBoletoResultado>('/api/sicoob-boleto/emitir', {
      contaCorrenteId,
      contaReceberId,
      opcoes: {
        multaPercentual: opcoes.multaPercentual ?? 0,
        moraPercentual: opcoes.moraPercentual ?? 0,
        descontoPercentual: opcoes.descontoPercentual ?? 0,
        mensagem: opcoes.mensagem,
        // null = padrão do contrato do cooperado (ex.: boleto híbrido com QR).
        comPix: opcoes.comPix ?? null,
      },
    });
    return {
      codigoSolicitacao: r.nossoNumero,
      contaReceberId: r.contaReceberId,
      valor: r.valor,
      dtVencimento: r.dtVencimento,
    };
  },

  async listarCobrancas(contaCorrenteId, filtro) {
    const { data } = await apiClient.get<CobrancasEmitidasPagina>('/api/sicoob-boleto', {
      params: {
        contaCorrenteId,
        dtInicio: filtro.dtInicio || undefined,
        dtFim: filtro.dtFim || undefined,
        busca: filtro.busca?.trim() || undefined,
        pagina: filtro.pagina ?? 1,
        tamanhoPagina: filtro.tamanhoPagina ?? 20,
      },
    });
    return data;
  },

  async consultarCobranca(contaCorrenteId, nossoNumero): Promise<CobrancaSituacao> {
    const { data: r } = await apiClient.get<SicoobBoletoSituacao>(
      `/api/sicoob-boleto/${encodeURIComponent(nossoNumero)}`,
      { params: { contaCorrenteId } },
    );
    return {
      codigoSolicitacao: r.nossoNumero,
      situacao: r.situacao,
      valorNominal: r.valorNominal,
      dtVencimento: r.dtVencimento,
      linhaDigitavel: r.linhaDigitavel,
      codigoBarras: r.codigoBarras,
      pixCopiaECola: r.pixCopiaECola,
      pixTxid: null,
      baixaAplicada: r.baixaAplicada,
    };
  },

  async baixarCobrancaPdf(contaCorrenteId, nossoNumero) {
    const { data } = await apiClient.get<Blob>(
      `/api/sicoob-boleto/${encodeURIComponent(nossoNumero)}/pdf`,
      { params: { contaCorrenteId }, responseType: 'blob' },
    );
    return data;
  },

  /// No Sicoob o cancelamento é a "baixa" do boleto (não pede motivo).
  async cancelarCobranca(contaCorrenteId, nossoNumero) {
    await apiClient.post(
      `/api/sicoob-boleto/${encodeURIComponent(nossoNumero)}/baixar`,
      { contaCorrenteId },
    );
  },

  // ── PIX ──────────────────────────────────────────────────────────────────────
  async criarPixImediato(contaCorrenteId, req: PixCobRequest) {
    const { data: r } = await apiClient.post<PixCobResultado>('/api/sicoob-pix/cob', {
      contaCorrenteId, cob: req,
    });
    return r;
  },

  async criarPixComVencimento(contaCorrenteId, req: PixCobvRequest) {
    const { data: r } = await apiClient.post<PixCobResultado>('/api/sicoob-pix/cobv', {
      contaCorrenteId, cobv: req,
    });
    return r;
  },

  async consultarPix(contaCorrenteId, txid) {
    const { data: r } = await apiClient.get<PixCobResultado>(
      `/api/sicoob-pix/${encodeURIComponent(txid)}`,
      { params: { contaCorrenteId } },
    );
    return r;
  },

  async devolverPix(contaCorrenteId, e2eid, valor, motivo): Promise<PixDevolucaoResultado> {
    const { data: r } = await apiClient.post<PixDevolucaoResultado>(
      `/api/sicoob-pix/${encodeURIComponent(e2eid)}/devolucao`,
      { contaCorrenteId, valor, motivo },
    );
    return r;
  },

  // ── Webhooks ──────────────────────────────────────────────────────────────────
  async registrarWebhook(contaCorrenteId, tipo: WebhookTipo, webhookUrl) {
    await apiClient.put('/api/sicoob-webhook-admin/registrar', {
      contaCorrenteId, tipo: tipoWebhook(tipo), webhookUrl,
    });
  },

  async consultarWebhook(contaCorrenteId, tipo: WebhookTipo) {
    const { data } = await apiClient.get<{ webhookUrl: string | null }>(
      '/api/sicoob-webhook-admin/consultar',
      { params: { contaCorrenteId, tipo: tipoWebhook(tipo) } },
    );
    return data.webhookUrl;
  },

  async excluirWebhook(contaCorrenteId, tipo: WebhookTipo) {
    await apiClient.delete('/api/sicoob-webhook-admin/excluir', {
      params: { contaCorrenteId, tipo: tipoWebhook(tipo) },
    });
  },
};
